import type { GameContext } from '../core/context';
import { angleDelta, damp, lerpAngle } from '../core/math';
import type { System } from '../core/system';
import { Rng } from '../core/rng';
import {
  archetypeStyle,
  attentionNear,
  createCharacterRig,
  PED_ARCHETYPES,
  POLICE_ARMED,
  POLICE_CONSTABLE,
  type CharacterStyle,
  createFootstepBus,
  hitFlash,
  latestHit,
  PLAYER_VILLAIN,
  playerSpot,
  relativeYaw,
  type AnimInput,
  type CharacterFootstep,
  type CharacterRig,
  type FootstepBus,
  type HitReaction,
} from '../render/characters';
import { qualityTier } from '../render/qualityTier';
import type { DetailedFigure } from '../render/characters/detailedPlayer';
import { playerAction } from './playerAnim';
import type { PlayerService } from './playerSystem';

/**
 * The player character: a 1970 South London villain on the shared character rig — knee-length
 * camel overcoat with lapels, collar and a swinging skirt, shirt and tie, tweed flat cap over a side
 * parting, mutton-chop sideburns and moustache, big readable hands with fingers and a thumb, shoes
 * with soles and heels. Drawn at 1.22× so the silhouette reads from the high camera.
 *
 * Animation is driven read-only from the PlayerService (and police/economy outcome): locomotion with
 * start/stop/turn, foot roll and follow-through, idle habits (a look at the watch, hands in pockets,
 * blown after a sprint), enter/exit (steal variant for carjacks and break-ins), knockdown → get-up,
 * busted (hands up, knees) and wasted, plus the weapon layer (carry, draw, aim, combos, recoil,
 * reload) read from the `weapons` service, the head turning to a shot nearby, and a flinch when hurt.
 * Footsteps are published on the `characterFootsteps` service; where the player stands is published
 * for the crowd's heads (characterLook.ts). Nothing here writes game state.
 */
const SCALE = 1.22;

declare module '../core/services' {
  interface ServiceMap {
    /** The player's character rig (read-only for attachments: src/weapons hangs the weapon in hand on it). */
    playerRig: CharacterRig;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /**
     * Look-development aid (docs/POLISH.md, characters second pass): stand one figure of every
     * pedestrian archetype plus a constable and an armed officer in a row at (x, z), facing +Z, idling.
     * `seed` varies the draw; returns how many were placed. `characterLineup(null)` clears them.
     */
    characterLineup(x: number | null, z?: number, seed?: number): number;
    /**
     * Which figure is drawn (docs/POLISH.md "Detailed figures"): 'detailed' (the Meshy model, the look),
     * 'loading', 'failed' (the rig's own body stands in) or 'classic' (`?player=classic`, developers only).
     */
    playerModel(): string;
    /**
     * Perf stress for the detailed player: stand `n` extra detailed figures (walking on the spot) in a
     * ring round (x, z), or round the player without a position; 0 clears them. `model` 'police' stands
     * detailed constables instead (same cost class), 'remote' players in other players' coat colours (as
     * src/net/netView.ts draws them online). Returns how many stand.
     */
    detailedCrowd(n: number, x?: number, z?: number, model?: 'player' | 'police' | 'remote'): Promise<number>;
  }
}

/** Coat colours for the `detailedCrowd(n, …, 'remote')` preview (navy, oxblood, bottle green, mustard). */
const REMOTE_PREVIEW_COATS = [0x2b3550, 0x5e2427, 0x2f4a36, 0x9a7a2a];

/** `?player=classic`: a developer switch back to the low-poly rig's own body (not in any menu). */
function classicByUrl(): boolean {
  return typeof location !== 'undefined' && new URLSearchParams(location.search).get('player') === 'classic';
}

export function createPlayerViewSystem(): System {
  let ctx: GameContext;
  let player: PlayerService;
  let rig: CharacterRig;
  let steps: FootstepBus | null = null;
  const pose = { x: 0, z: 0, angle: 0 };
  const step: CharacterFootstep = { x: 0, z: 0, side: 'left', speed: 0, intensity: 0, source: 'player' };
  const input: AnimInput = { dt: 0, speed: 0, running: false, turnRate: 0, action: 'none', actionProgress: 0, heading: 0 };
  const hit: HitReaction = { t: 0, dir: 0, strength: 0 };
  let hurtAt = -99;
  let hurtStrength = 0;
  let heading = 0;
  let speed = 0;
  let lastX = 0;
  let lastZ = 0;
  let snap = true;
  let lastOutcome: 'none' | 'busted' | 'wasted' = 'none';
  const offs: (() => void)[] = [];
  const lineup: CharacterRig[] = [];
  const lineupInput: AnimInput = { dt: 0, speed: 0, running: false, turnRate: 0, action: 'none', actionProgress: 0, heading: 0 };
  // The detailed figure, its load state and the perf-stress copies.
  let detailed: DetailedFigure | null = null;
  let modelState: 'classic' | 'loading' | 'detailed' | 'failed' = 'classic';
  let alive = true;
  const crowd: { rig: CharacterRig; fig: DetailedFigure }[] = [];
  const crowdInput: AnimInput = { dt: 0, speed: 1.4, running: false, turnRate: 0, action: 'none', actionProgress: 0, heading: 0 };
  type DetailedModule = typeof import('../render/characters/detailedPlayer');
  const loadDetailed = async (id: 'player' | 'police' = 'player'): Promise<[DetailedModule, Awaited<ReturnType<DetailedModule['loadDetailedModel']>>]> => {
    const m = await import('../render/characters/detailedPlayer');
    return [m, await m.loadDetailedModel(id, qualityTier() === 'low' ? 'low' : 'high')];
  };

  /**
   * Put the detailed figure on the rig (loads once, async). Until it is in — or if it never arrives —
   * the rig's own body draws: a silent technical fallback, never a choice.
   */
  function useDetailed(): void {
    if (!ctx.render || classicByUrl()) return;
    modelState = 'loading';
    loadDetailed()
      .then(([m, gltf]) => {
        if (detailed || !alive) return;
        detailed = m.createDetailedFigure(gltf, rig.skeleton);
        rig.root.add(detailed.root);
        rig.setBodyVisible(false);
        rig.handRSocket = detailed.handR;
        modelState = 'detailed';
      })
      .catch((e: unknown) => {
        modelState = 'failed';
        console.warn('[player] detailed model failed to load; drawing the rig body instead', e);
      });
  }

  function outcome(): 'none' | 'busted' | 'wasted' {
    const police = ctx.services.tryGet('police')?.outcome ?? 'none';
    if (police !== 'none') return police;
    return ctx.services.tryGet('economy')?.outcomePending ?? 'none';
  }

  return {
    id: 'player-view',
    order: 711,
    init(c) {
      ctx = c;
      player = c.services.get('player');
      rig = createCharacterRig(PLAYER_VILLAIN, { scale: SCALE, seed: 0x7a11 });
      rig.root.name = 'player';
      c.services.provide('playerRig', rig);
      if (!c.services.tryGet('characterFootsteps')) {
        steps = createFootstepBus();
        c.services.provide('characterFootsteps', steps);
      }
      rig.onFootstep = (e) => {
        if (!steps) return;
        step.x = rig.root.position.x + e.dx;
        step.z = rig.root.position.z + e.dz;
        step.side = e.foot;
        step.speed = e.speed;
        step.intensity = e.intensity;
        steps.publish(step);
      };
      c.hooks.register('characterLineup', (x, z = 0, seed = 7) => {
        for (const r of lineup) r.dispose();
        lineup.length = 0;
        if (x === null) return 0;
        const rng = new Rng(seed);
        const styles: CharacterStyle[] = [...PED_ARCHETYPES.map((k) => archetypeStyle(k, rng)), POLICE_CONSTABLE, POLICE_ARMED];
        styles.forEach((s, i) => {
          const r = createCharacterRig(s, { scale: 1.18, seed: seed * 31 + i });
          r.root.position.set(x + (i - (styles.length - 1) / 2) * 1.15, 0, z);
          ctx.scene.add(r.root);
          lineup.push(r);
        });
        return lineup.length;
      });
      c.hooks.register('playerModel', () => modelState);
      c.hooks.register('detailedCrowd', async (n, x, z, model = 'player') => {
        for (const e of crowd) {
          e.fig.dispose();
          e.rig.dispose();
        }
        crowd.length = 0;
        if (n <= 0 || !ctx.render) return 0;
        const [m, gltf] = await loadDetailed(model === 'police' ? 'police' : 'player');
        const cx = x ?? player.pos.x;
        const cz = z ?? player.pos.z;
        for (let i = 0; i < n; i++) {
          const r = createCharacterRig(model === 'police' ? POLICE_CONSTABLE : PLAYER_VILLAIN, { scale: model === 'police' ? 1.22 : SCALE, seed: 0x5eed + i });
          r.setBodyVisible(false);
          const fig = m.createDetailedFigure(gltf, r.skeleton, model === 'remote' ? { tint: REMOTE_PREVIEW_COATS[i % REMOTE_PREVIEW_COATS.length] } : {});
          r.root.add(fig.root);
          const a = (i / n) * Math.PI * 2;
          r.root.position.set(cx + Math.cos(a) * 2.6, 0, cz + Math.sin(a) * 2.6);
          r.root.rotation.y = -a;
          ctx.scene.add(r.root);
          crowd.push({ rig: r, fig });
        }
        return crowd.length;
      });
      offs.push(
        c.events.on('playerTeleported', () => (snap = true)),
        c.events.on('playerHurt', (e) => {
          // A flinch (gunshots come with a direction through the police shot; this covers the rest).
          if (e.cause === 'shot') return;
          hurtAt = c.state.time;
          hurtStrength = Math.min(1, 0.3 + e.amount / 40);
        }),
      );
      ctx.scene.add(rig.root);
    },
    start() {
      useDetailed();
    },
    update(dt, alpha) {
      const mode = player.mode;
      const out = outcome();
      // Respawn after an outcome: stand back up fresh (wasted is terminal in the animator).
      if (lastOutcome !== 'none' && out === 'none') {
        rig.animator.reset('idle');
        snap = true;
      }
      lastOutcome = out;
      if (lineup.length) {
        lineupInput.dt = dt;
        for (const r of lineup) r.update(lineupInput);
      }
      if (crowd.length) {
        crowdInput.dt = dt;
        for (const e of crowd) {
          e.rig.update(crowdInput);
          e.fig.sync(e.rig, dt);
        }
      }
      rig.root.visible = mode !== 'driving' && mode !== 'riding';
      const weaponAnim = ctx.services.tryGet('weapons')?.anim ?? null;
      playerSpot.valid = true;
      playerSpot.x = player.pos.x;
      playerSpot.z = player.pos.z;
      playerSpot.armed = !!weaponAnim && weaponAnim.hold !== 'fist' && weaponAnim.hold !== 'none' && weaponAnim.raise > 0.3;
      if (!rig.root.visible) {
        snap = true;
        return;
      }
      player.body.interpolate(alpha, pose);
      const target = player.heading;
      if (snap) {
        heading = target;
        lastX = pose.x;
        lastZ = pose.z;
        speed = 0;
        snap = false;
      }
      // Visible speed from the rendered displacement, so stride matches what's on screen.
      const prevHeading = heading;
      if (dt > 0) {
        const raw = Math.hypot(pose.x - lastX, pose.z - lastZ) / dt;
        speed += ((raw > 20 ? 0 : raw) - speed) * damp(14, dt);
        heading = lerpAngle(heading, target, damp(16, dt));
      }
      lastX = pose.x;
      lastZ = pose.z;
      rig.root.position.set(pose.x, 0, pose.z);
      rig.root.rotation.y = heading;

      const v = player.vehicle;
      const now = ctx.state.time;
      input.dt = dt;
      input.speed = speed < 0.05 ? 0 : speed;
      input.running = player.running;
      input.turnRate = dt > 0 ? angleDelta(prevHeading, heading) / dt : 0;
      input.action = playerAction({
        mode,
        outcome: out,
        vehicleHasDriver: v?.occupant?.kind === 'ped',
        vehicleLocked: !!v && v.locked && !v.ownedByPlayer && !v.stolen,
      });
      input.actionProgress = player.actionProgress;
      input.heading = heading;
      input.weapon = weaponAnim;
      // Turn the head to a shot or a crash nearby (not our own).
      const at = attentionNear(pose.x, pose.z, 22, now);
      input.lookWeight = 0;
      if (at && Math.hypot(at.x - pose.x, at.z - pose.z) > 2.5 && !(weaponAnim && weaponAnim.raise > 0.3)) {
        input.lookYaw = relativeYaw(pose.x, pose.z, heading, at.x, at.z);
        input.lookWeight = 0.9;
      }
      // Flinch / stagger: a police bullet (with its direction) or any other hurt.
      input.hit = null;
      const shot = latestHit('player', 0, now - 1.2);
      if (shot) {
        hit.t = now - shot.time;
        hit.dir = relativeYaw(pose.x, pose.z, heading, shot.fromX, shot.fromZ);
        hit.strength = shot.strength;
        input.hit = hit;
      } else if (now - hurtAt < 1.2) {
        hit.t = now - hurtAt;
        hit.dir = 0;
        hit.strength = hurtStrength;
        input.hit = hit;
      }
      rig.update(input);
      detailed?.sync(rig, dt, weaponAnim);
      detailed?.setHit(input.hit ? hitFlash(hit.t, hit.strength) : 0);
    },
    dispose() {
      alive = false;
      for (const e of crowd) {
        e.fig.dispose();
        e.rig.dispose();
      }
      crowd.length = 0;
      detailed?.dispose();
      detailed = null;
      for (const off of offs) off();
      offs.length = 0;
      for (const r of lineup) r.dispose();
      lineup.length = 0;
      playerSpot.valid = false;
      rig.dispose();
    },
  };
}
